import React, {
  useState, useCallback, forwardRef, useImperativeHandle, useRef, useEffect,
} from 'react';

import {
  Form, Button, notification, Select,
} from 'antd';
import * as Yup from 'yup';

import useManager from '../../../hooks/manager';
import api from '../../../services/api';
import Modal from '../../Modal';

const UpdateForm = ({ onSuccess }, ref) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [matrizCompany, setMatrizCompany] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const { update } = useManager();
  const formRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/company');
        setCompanies(data);
      } catch (err) {
        console.error('Failed to load companies', err);
      }
    })();
  }, []);

  const open = useCallback((openId) => {
    (async () => {
      try {
        setLoading(true);
        setShowModal(true);
        const { data } = await api.get(`/matriz-filial/${openId}`);
        setId(openId);
        setMatrizCompany(data.matriz?.mongoId || null);
        setFiliais((data.filiais || []).map((f) => f.mongoId));
      } catch (error) {
        console.error('Failed to load matriz/filial', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useImperativeHandle(ref, () => ({
    open,
  }));

  const filialOptions = companies.filter((c) => String(c._id) !== String(matrizCompany));

  const handleSubmit = useCallback(() => {
    (async () => {
      try {
        setLoading(true);
        const data = {
          matrizCompany,
          filiais,
        };

        const schema = Yup.object().shape({
          matrizCompany: Yup.string().required('Selecione a empresa matriz'),
          filiais: Yup.array().min(1, 'Selecione ao menos uma filial').required(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        update(id, '/matriz-filial', data, () => {
          setValidationErrors({});
          setShowModal(false);
          if (onSuccess) {
            onSuccess();
          }
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err);
        setValidationErrors({});
        if (err instanceof Yup.ValidationError) {
          notification.error({
            message: 'Falha na Validação',
          });
          const ve = {};
          err.inner.forEach((e) => {
            ve[e.path] = e.message;
          });
          setValidationErrors(ve);
          return;
        }

        if (err && err.response && err.response.data) {
          notification.error({
            message: err.response.data.message,
          });
          return;
        }

        notification.error({
          message: 'Falha ao salvar',
        });
      }
    })();
  }, [update, id, matrizCompany, filiais, onSuccess]);

  return (
    <Modal
      title="Alterar Matriz e Filial"
      visible={showModal}
      loading={loading}
      footer={(
        <Button type="primary" size="large" onClick={handleSubmit}>
          Alterar
        </Button>
      )}
      onClose={() => setShowModal(false)}
    >
      <Form ref={formRef} size="large">
        <Form.Item
          validateStatus={validationErrors.matrizCompany ? 'error' : ''}
          help={validationErrors.matrizCompany}
          hasFeedback
        >
          <Select
            placeholder="Empresa matriz"
            value={matrizCompany}
            onChange={(value) => {
              setMatrizCompany(value);
              setFiliais((prev) => prev.filter((fid) => String(fid) !== String(value)));
            }}
            showSearch
            optionFilterProp="children"
          >
            {companies.map((c) => (
              <Select.Option key={c._id} value={c._id}>
                {c.fantasyName}
                {' - '}
                {c.name}
                {c.cnpj ? ` (${c.cnpj})` : ''}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          validateStatus={validationErrors.filiais ? 'error' : ''}
          help={validationErrors.filiais}
          hasFeedback
        >
          <Select
            mode="multiple"
            placeholder="Empresas filiais"
            value={filiais}
            onChange={setFiliais}
            showSearch
            optionFilterProp="children"
            disabled={!matrizCompany}
          >
            {filialOptions.map((c) => (
              <Select.Option key={c._id} value={c._id}>
                {c.fantasyName}
                {' - '}
                {c.name}
                {c.cnpj ? ` (${c.cnpj})` : ''}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default forwardRef(UpdateForm);
